/**
 * Handles all the calculations for vendor invoice items.
 * Used inside of the VendorInvoiceTotals service.
 */
export function VendorInvoiceItemTotalsService ( bigDecimalApi, sessionObjAPI ) {
  var VendorInvoiceItemTotals = function (invoice) {
    this.invoice = invoice;
    this.info = sessionObjAPI.currency();
    this.company = sessionObjAPI.company();
  };

  /**
   * Determines whether or not the invoice is closed based on status
   * @return {boolean} true if closed, false if open
   */
  VendorInvoiceItemTotals.prototype.closed = function () {
    var self = this;

    return self.invoice.invoice_status === 'P' || self.invoice.invoice_status === 'C';
  };

  /**
   * Calculates the total of the invoice item
   * @param  {object} item The invoice item to calculate the total
   * @return {string}      The total of the invoice item
   */
  VendorInvoiceItemTotals.prototype.total = function (item) {
    const self = this;

    const cost = self.invoice.price_includes_tax
      ? item.cost_including_tax
      : item.unit_cost;

    const quantity = bigDecimalApi.get(item.quantity, 2);
    const taxRate  = bigDecimalApi.get(item.purchase_tax_rate, 2);
    const unitCost = bigDecimalApi.get(cost, 4);

    const total = unitCost.multiply(quantity);

    // If invoice is closed, just return the server value
    if (self.closed()) {
      return item.total || '0';
    }

    // If the product is tax free or the price includes tax, return the total
    if ( item.gst_free || self.invoice.price_includes_tax ) {
      return total.toString();
    }

    // If not, add in the tax to the total
    else {
      return total
        .multiply
          (taxRate.movePointLeft(2).setScale(2, BigDecimal.ROUND_HALF_UP) )
            .add(total).setScale(2, BigDecimal.ROUND_HALF_UP).toString();
    }
  };

  /**
   * Calculates the tax of the invoice item
   * @param  {object} item The invoice item to calculate the tax
   * @return {string}      The total tax of the invoice item
   */
  VendorInvoiceItemTotals.prototype.tax = function (item) {
    const self = this;

    let tax = bigDecimalApi.get();

    const cost = self.invoice.price_includes_tax
      ? item.cost_including_tax
      : item.unit_cost;

    const quantity = bigDecimalApi.get(item.quantity, 2);
    const taxRate  = bigDecimalApi.get(item.purchase_tax_rate, 2);
    const unitCost = bigDecimalApi.get(cost, 4);

    const total = unitCost.multiply(quantity);

    // If invoice is closed, just return the server value
    if (self.closed()) {
      return item.gst || '0';
    }

    // If product is tax free then obviously return 0 for tax
    else if (item.gst_free) {
      return '0';
    }

    // If the product includes tax, extract out the tax from the total
    else if (self.invoice.price_includes_tax) {
      tax = total
        .subtract( total.divide( BigDecimal.ONE.add(taxRate.movePointLeft(2)) ) )
          .setScale(2, BigDecimal.ROUND_HALF_UP).toString();
    }

    // If not then just calculate it based on the tax rate
    else {
      tax = total
        .multiply(taxRate.movePointLeft(2))
          .setScale(2, BigDecimal.ROUND_HALF_UP).toString();
    }

    return tax;
  };

  return VendorInvoiceItemTotals;
}